import { useEffect, useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const MONTH_NAMES = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
];
const DOW_NAMES = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const SHORTCUTS = {
  "@yearly": "0 0 1 1 *",
  "@annually": "0 0 1 1 *",
  "@monthly": "0 0 1 * *",
  "@weekly": "0 0 * * 0",
  "@daily": "0 0 * * *",
  "@midnight": "0 0 * * *",
  "@hourly": "0 * * * *",
};

const FIELD_DEFS = [
  { key: "minute", min: 0, max: 59, names: null },
  { key: "hour", min: 0, max: 23, names: null },
  { key: "dom", min: 1, max: 31, names: null },
  { key: "month", min: 1, max: 12, names: MONTH_NAMES },
  { key: "dow", min: 0, max: 6, names: DOW_NAMES },
];

const PRESETS = [
  { labelKey: "cron_translator_preset_every_minute", expr: "* * * * *" },
  { labelKey: "cron_translator_preset_hourly", expr: "0 * * * *" },
  { labelKey: "cron_translator_preset_daily", expr: "0 0 * * *" },
  { labelKey: "cron_translator_preset_weekdays_9am", expr: "0 9 * * 1-5" },
  { labelKey: "cron_translator_preset_every_15_min", expr: "*/15 * * * *" },
  { labelKey: "cron_translator_preset_first_of_month", expr: "0 0 1 * *" },
  { labelKey: "cron_translator_preset_sunday_midnight", expr: "0 0 * * 0" },
];

const DEFAULT_EXPR = "*/15 9-17 * * 1-5";

const parseValue = (token, min, max, names) => {
  if (token == null || token === "") {
    throw new Error(`Empty value`);
  }
  const lower = token.toLowerCase();
  if (names) {
    const idx = names.indexOf(lower);
    if (idx !== -1) return idx + (names === MONTH_NAMES ? 1 : 0);
  }
  if (!/^\d+$/.test(token)) {
    throw new Error(`Invalid token: ${token}`);
  }
  const n = parseInt(token, 10);
  if (n < min || n > max) {
    if (names === DOW_NAMES && n === 7) return 0;
    throw new Error(`Out of range: ${token}`);
  }
  return n;
};

const parseField = (raw, def) => {
  const { min, max, names } = def;
  const result = new Set();
  const parts = raw.split(",");
  for (const part of parts) {
    if (part === "") throw new Error(`Empty segment in ${raw}`);
    const [rangePart, stepPart] = part.split("/");
    const step = stepPart != null ? parseInt(stepPart, 10) : 1;
    if (!Number.isInteger(step) || step < 1) {
      throw new Error(`Invalid step in ${part}`);
    }
    let start;
    let end;
    if (rangePart === "*") {
      start = min;
      end = max;
    } else if (rangePart.includes("-")) {
      const [a, b] = rangePart.split("-");
      start = parseValue(a, min, max, names);
      end = parseValue(b, min, max, names);
    } else {
      start = parseValue(rangePart, min, max, names);
      end = stepPart != null ? max : start;
    }
    if (start > end) throw new Error(`Reversed range in ${part}`);
    for (let i = start; i <= end; i += step) result.add(i);
  }
  return [...result].sort((a, b) => a - b);
};

const expandShortcut = (input) => {
  const trimmed = input.trim().toLowerCase();
  if (SHORTCUTS[trimmed]) return SHORTCUTS[trimmed];
  return input.trim();
};

const parseCron = (input) => {
  const expr = expandShortcut(input);
  const tokens = expr.split(/\s+/);
  if (tokens.length !== 5) {
    throw new Error(`Expected 5 fields, got ${tokens.length}`);
  }
  const fields = {};
  const tokenMap = {};
  FIELD_DEFS.forEach((def, i) => {
    fields[def.key] = parseField(tokens[i], def);
    tokenMap[def.key] = tokens[i];
  });
  const domRestricted = tokenMap.dom !== "*";
  const dowRestricted = tokenMap.dow !== "*";
  return {
    fields,
    tokens: tokenMap,
    expandedExpr: expr,
    domRestricted,
    dowRestricted,
  };
};

const computeNextRuns = (parsed, fromDate, count) => {
  const { fields, domRestricted, dowRestricted } = parsed;
  const minuteSet = new Set(fields.minute);
  const hourSet = new Set(fields.hour);
  const monthSet = new Set(fields.month);
  const domSet = new Set(fields.dom);
  const dowSet = new Set(fields.dow);

  const results = [];
  const date = new Date(fromDate.getTime());
  date.setSeconds(0, 0);
  date.setMinutes(date.getMinutes() + 1);

  const startYear = date.getFullYear();
  let safety = 0;
  while (results.length < count && safety < 200000) {
    safety += 1;
    if (date.getFullYear() > startYear + 8) break;

    if (!monthSet.has(date.getMonth() + 1)) {
      date.setMonth(date.getMonth() + 1, 1);
      date.setHours(0, 0, 0, 0);
      continue;
    }

    const dom = date.getDate();
    const dow = date.getDay();
    let dayMatch;
    if (domRestricted && dowRestricted) {
      dayMatch = domSet.has(dom) || dowSet.has(dow);
    } else if (domRestricted) {
      dayMatch = domSet.has(dom);
    } else if (dowRestricted) {
      dayMatch = dowSet.has(dow);
    } else {
      dayMatch = true;
    }
    if (!dayMatch) {
      date.setDate(date.getDate() + 1);
      date.setHours(0, 0, 0, 0);
      continue;
    }

    if (!hourSet.has(date.getHours())) {
      date.setHours(date.getHours() + 1, 0, 0, 0);
      continue;
    }

    if (!minuteSet.has(date.getMinutes())) {
      date.setMinutes(date.getMinutes() + 1, 0, 0);
      continue;
    }

    results.push(new Date(date.getTime()));
    date.setMinutes(date.getMinutes() + 1);
  }
  return results;
};

const formatRelativeDuration = (deltaSeconds, t) => {
  const abs = Math.max(0, Math.floor(deltaSeconds));
  let value;
  let unitKey;
  if (abs < 60) {
    value = abs;
    unitKey = value === 1 ? "cron_translator_unit_second" : "cron_translator_unit_seconds";
  } else if (abs < 3600) {
    value = Math.floor(abs / 60);
    unitKey = value === 1 ? "cron_translator_unit_minute" : "cron_translator_unit_minutes";
  } else if (abs < 86400) {
    value = Math.floor(abs / 3600);
    unitKey = value === 1 ? "cron_translator_unit_hour" : "cron_translator_unit_hours";
  } else {
    value = Math.floor(abs / 86400);
    unitKey = value === 1 ? "cron_translator_unit_day" : "cron_translator_unit_days";
  }
  return t("cron_translator_duration_format", { value, unit: t(unitKey) });
};

const formatLocal = (date) =>
  date.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatUtc = (date) =>
  date.toUTCString().replace(" GMT", " UTC");

const summarizeField = (def, values, token, t) => {
  const { min, max, names } = def;
  const allValues = max - min + 1;
  if (values.length === allValues) return t("cron_translator_summary_any");
  if (token.startsWith("*/")) {
    const step = parseInt(token.slice(2), 10);
    if (Number.isInteger(step) && step > 0) {
      return t("cron_translator_summary_every_step", { step });
    }
  }
  const formatted = values.map((v) => {
    if (names) {
      const idx = names === MONTH_NAMES ? v - 1 : v;
      const key = `cron_translator_${def.key}_name_${idx}`;
      return t(key);
    }
    return String(v);
  });
  if (values.length === 1) {
    return t("cron_translator_summary_at_one", { value: formatted[0] });
  }
  if (values.length <= 6) {
    return t("cron_translator_summary_at_list", { values: formatted.join(", ") });
  }
  return t("cron_translator_summary_at_list_truncated", {
    values: formatted.slice(0, 6).join(", "),
    more: values.length - 6,
  });
};

const CronTranslator = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(DEFAULT_EXPR);
  const [now, setNow] = useState(() => new Date());
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => (n + 1) % 1_000_000), 30_000);
    return () => clearInterval(id);
  }, []);

  const parseResult = useMemo(() => {
    try {
      return { ok: true, data: parseCron(input) };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  }, [input]);

  const nextRuns = useMemo(() => {
    if (!parseResult.ok) return [];
    return computeNextRuns(parseResult.data, now, 5);
  }, [parseResult, now]);

  const onPreset = (expr) => {
    setInput(expr);
    setNow(new Date());
  };

  const onRefresh = () => setNow(new Date());

  const copy = async (text, label) => {
    if (!text) {
      toast.error(t("cron_translator_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("cron_translator_title")}
            </h1>
            <p className="text-gray-600">{t("cron_translator_intro")}</p>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("cron_translator_input_label")}
              </label>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("cron_translator_placeholder")}
                className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              />
              <div className="text-xs text-gray-500">
                {t("cron_translator_format_hint")}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-700">
                {t("cron_translator_presets_label")}
              </span>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <StandardButton
                    key={p.expr}
                    onClick={() => onPreset(p.expr)}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t(p.labelKey)}
                  </StandardButton>
                ))}
              </div>
            </div>

            {!parseResult.ok && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm">
                <span className="font-semibold">
                  {t("cron_translator_invalid")}:
                </span>{" "}
                {parseResult.error}
              </div>
            )}

            {parseResult.ok && (
              <>
                <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-indigo-700 uppercase">
                      {t("cron_translator_breakdown_label")}
                    </span>
                    <StandardButton
                      onClick={() =>
                        copy(
                          parseResult.data.expandedExpr,
                          t("cron_translator_expression_label")
                        )
                      }
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("copy_button")}
                    </StandardButton>
                  </div>
                  <ul className="flex flex-col divide-y divide-indigo-100">
                    {FIELD_DEFS.map((def) => {
                      const values = parseResult.data.fields[def.key];
                      const token = parseResult.data.tokens[def.key];
                      return (
                        <li
                          key={def.key}
                          className="grid grid-cols-12 gap-2 py-2 items-baseline"
                        >
                          <span className="col-span-3 text-xs font-semibold text-indigo-700 uppercase">
                            {t(`cron_translator_field_${def.key}`)}
                          </span>
                          <span className="col-span-2 font-mono text-sm text-gray-700 break-all">
                            {token}
                          </span>
                          <span className="col-span-7 text-sm text-gray-800">
                            {summarizeField(def, values, token, t)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="bg-white p-4 rounded-md border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-indigo-700 uppercase">
                      {t("cron_translator_next_runs_label")}
                    </span>
                    <StandardButton
                      onClick={onRefresh}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("cron_translator_refresh")}
                    </StandardButton>
                  </div>
                  {nextRuns.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t("cron_translator_no_runs")}
                    </p>
                  ) : (
                    <ol className="flex flex-col divide-y divide-gray-100">
                      {nextRuns.map((d, i) => {
                        const deltaSec = (d.getTime() - Date.now()) / 1000;
                        const relative = formatRelativeDuration(deltaSec, t);
                        return (
                          <li
                            key={i}
                            className="grid grid-cols-12 gap-2 py-2 items-baseline"
                          >
                            <span className="col-span-1 text-xs font-semibold text-indigo-700">
                              #{i + 1}
                            </span>
                            <span className="col-span-5 text-sm text-gray-800">
                              <span className="block text-xs uppercase text-gray-500">
                                {t("cron_translator_local_time")}
                              </span>
                              {formatLocal(d)}
                              <span className="block text-xs text-indigo-600 mt-0.5">
                                {t("cron_translator_runs_in", { relative })}
                              </span>
                            </span>
                            <span className="col-span-6 text-sm text-gray-800">
                              <span className="block text-xs uppercase text-gray-500">
                                {t("cron_translator_utc_time")}
                              </span>
                              {formatUtc(d)}
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              </>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("cron_translator_note_format")}</p>
              <p>{t("cron_translator_note_shortcuts")}</p>
              <p>{t("cron_translator_note_dow")}</p>
              <p>{t("cron_translator_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default CronTranslator;
